import React, { FC, useState, useEffect } from 'react'
import {
    Platform,
    RefreshControl,
    ScrollView,
    StyleSheet,
    View,
    useWindowDimensions,
} from 'react-native'
import { NavigationProp, ParamListBase, useIsFocused, useNavigation } from '@react-navigation/native';
import moment from 'moment';
import { Theme, ThemeConsumer } from '@rneui/themed';
import { shallowEqual } from 'react-redux';

// Common components
import CommonHeader from '../../shared/commonComponents/commonHeader'
import { ZenBadge, ZenEmptyComponent, ZenLoader, ZenText, ZenButton, ZenIcon } from 'zen-core-elements'
import { useAppRoute } from '../../types/@react-navigation/navigation';
import { config } from '../../shared/environment';
import { getScreenPermission, useAppDispatch, useAppSelector } from '../../redux/hooks';
import { getCustomerHistory } from '../../redux/slices/viewCustomerDetails.slice';
import { getOneCustomerDetails } from '../../redux/slices/customerSlice';
import { CustomerAddress } from '../customers/addEditCustomerModule';
import useBackHandler from '../../shared/hooks/useBackHandler';


const ViewCustomerDetails: FC = () => {
    const dispatch = useAppDispatch()
    useBackHandler()
    const { isWrite: isCustomerWrite } = useAppSelector(getScreenPermission("CustomerStack"), shallowEqual);
    const { isRead: isOrderRead } = useAppSelector(getScreenPermission("OrdersStack"), shallowEqual);

    const { params, name: routeName } = useAppRoute("ViewCustomerDetails")
    const isFocused = useIsFocused()
    const navigation = useNavigation<NavigationProp<ParamListBase>>()
    const { width, height } = useWindowDimensions()
    const isLandscape = width > height

    // ── Pull-to-refresh state ────────────────────────────────────────────────
    const [refresh, setRefresh] = useState(false)
    // ── Initial load state (until customer details for this id are fetched) ──
    const [initialLoad, setInitialLoad] = useState(true)

    // ── Redux state ──────────────────────────────────────────────────────────
    const customerDetails = useAppSelector(state => state.customer?.oneCustomerDetails?.customerDetails)
    const { values: customerHistory, cachedCustomerId } = useAppSelector(state => state?.viewCustomerDetails?.getCustomerHistory)
    const addressRows: CustomerAddress[] = customerDetails?.addresses || []

    // ── API: fetch customer history (cache-aware, used for joined date) ──────
    const fetchCustomerHistory = (forceRefresh = false) => {
        if (!forceRefresh && cachedCustomerId === params?.customerId) {
            setRefresh(false);
            return;
        }
        dispatch(getCustomerHistory({ storeId: config?.storeId, customerId: params?.customerId }))
            .unwrap()
            .catch((error) => console.log('error in getCustomerHistory', error))
            .finally(() => setRefresh(false));
    };

    // ── Pull-to-refresh handler ──────────────────────────────────────────────
    const handleRefresh = () => {
        setRefresh(true);
        dispatch(getOneCustomerDetails({ storeId: config?.storeId, customerId: params?.customerId }))
            .unwrap()
            .then(() => fetchCustomerHistory(true))
            .catch((error) => {
                console.log('error in getOneCustomerDetails (refresh)', error);
                setRefresh(false);
            });
    };

    // ── Initial fetch on focus ───────────────────────────────────────────────
    useEffect(() => {
        if (isFocused) {
            dispatch(getOneCustomerDetails({ storeId: config?.storeId, customerId: params?.customerId }))
                .unwrap()
                .then(() => fetchCustomerHistory())
                .catch((error) => console.log('error in getCustomer details', error))
                .finally(() => setInitialLoad(false));
        }
    }, [isFocused]);

    const fullName = `${customerDetails?.user?.firstName || ''} ${customerDetails?.user?.lastName || ''}`.trim()
    const initials = `${customerDetails?.user?.firstName?.[0] || ''}${customerDetails?.user?.lastName?.[0] || ''}`.toUpperCase()

    return (
        <ThemeConsumer>
            {({ theme }) => {
                const styles = style(theme)

                // ── Render: single label / value row in the information card ──
                const InfoRow = ({ label, value, icon, isLast }: { label: string, value?: string | null, icon: string, isLast?: boolean }) => (
                    <View style={[styles.infoRow, !isLast && styles.rowDivider]}>
                        <View style={styles.infoIconBox}>
                            <ZenIcon name={icon} type="material-community" size={18} color={theme?.primary} />
                        </View>
                        <View style={{ flex: 1, marginLeft: 12 }}>
                            <ZenText content={label} size={theme?.errorFontSize} color={theme?.disabledGray500} />
                            <ZenText content={value || '—'} size={theme?.smallerFontSize} color={theme?.lightDark} bold numberOfLines={1} ellipsizeMode="tail" style={{ marginTop: 2 }} />
                        </View>
                    </View>
                )

                // ── Render: Address card ──
                const AddressCard = ({ item }: { item: CustomerAddress }) => (
                    <View style={styles.addressCard}>
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            <View style={styles.infoIconBox}>
                                <ZenIcon
                                    type='material-community'
                                    name={item?.addressType?.toLowerCase() === 'home' ? 'home-variant-outline' : 'office-building-marker-outline'}
                                    size={18}
                                    color={theme?.primary}
                                />
                            </View>
                            <View style={{ flex: 1, marginLeft: 12 }}>
                                <ZenText content={item?.addressName || 'Unnamed Address'} bold size={theme?.smallerFontSize} color={theme?.lightDark} numberOfLines={1} />
                                <ZenText content={item?.addressType || 'Other'} size={theme?.errorFontSize} color={theme?.disabledGray500} style={{ textTransform: 'capitalize' }} />
                            </View>
                            {item?.isDefault && (
                                <View style={styles.defaultBadge}>
                                    <ZenText content="Default" size={theme?.badgeFontSize} bold color={theme?.primary} />
                                </View>
                            )}
                        </View>
                        <View style={styles.addressBody}>
                            <View style={styles.contactRow}>
                                <ZenIcon type='material-community' name="map-marker-outline" size={16} color={theme?.disabledGray500} />
                                <ZenText content={`${item?.addressLine1 || ''}${item?.addressLine2 ? ', ' + item?.addressLine2 : ''}`} color={theme?.lightDark} size={theme?.errorFontSize} style={{ marginLeft: 8, flex: 1, lineHeight: 20 }} />
                            </View>
                            {!!item?.mobileNumber && <View style={[styles.contactRow, { marginTop: 6 }]}>
                                <ZenIcon type='material-community' name="phone-outline" size={16} color={theme?.disabledGray500} />
                                <ZenText content={`${item?.countryCode ? '+' + item?.countryCode + ' ' : ''}${item?.mobileNumber}`} color={theme?.lightDark} size={theme?.errorFontSize} style={{ marginLeft: 8 }} />
                            </View>}
                        </View>
                    </View>
                )

                // ── Render: section card with title ──
                const SectionCard = ({ title, icon, count, children }: { title: string, icon: string, count?: number, children: React.ReactNode }) => (
                    <View style={[styles.sectionCard, isLandscape && { flex: 1 }]}>
                        <View style={styles.sectionHeader}>
                            <ZenIcon name={icon} type="material-community" size={20} color={theme?.primary} />
                            <ZenText content={title} bold size={theme?.smallHeaderFontSize} color={theme?.lightDark} style={{ marginLeft: 8, flex: 1 }} />
                            {count != undefined && (
                                <View style={styles.countBadge}>
                                    <ZenText content={String(count)} bold size={theme?.badgeFontSize} color={theme?.primary} />
                                </View>
                            )}
                        </View>
                        {children}
                    </View>
                )

                return (
                    <View style={styles.container}>
                        <CommonHeader headerName="Customer Details" backIcon={true} backIconOnpress={() => { navigation.goBack() }} />

                        {initialLoad && !customerDetails ? (
                            <ZenLoader lottieType="Local" lottie={require('../../assets/infinityLoader.json')} />
                        ) : (
                            <ScrollView
                                showsVerticalScrollIndicator={false}
                                contentContainerStyle={[styles.scrollContent, { padding: isLandscape ? 20 : 12 }]}
                                refreshControl={<RefreshControl refreshing={refresh} onRefresh={handleRefresh} colors={[theme?.primary]} />}
                            >
                                {/* -- Profile Card -- */}
                                <View style={[styles.profileCard, { flexDirection: isLandscape ? 'row' : 'column', alignItems: isLandscape ? 'center' : 'stretch' }]}>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', flex: isLandscape ? 1 : undefined }}>
                                        <View style={styles.avatar}>
                                            <ZenText content={initials || '?'} bold color={theme?.light} size={theme?.mediumFontSize} />
                                        </View>
                                        <View style={{ flex: 1, marginLeft: 14 }}>
                                            <ZenText content={fullName || '—'} bold size={theme?.largerFontSize} color={theme?.lightDark} numberOfLines={1} />
                                            <View style={styles.metaRow}>
                                                {!!customerDetails?.roles?.roleCode && <ZenBadge
                                                    badgeName={customerDetails?.roles?.roleCode}
                                                    badgeTextStyle={{ textTransform: "capitalize", fontSize: theme?.badgeFontSize, fontFamily: theme?.medium }}
                                                    badgeColor={theme?.tealPrimary}
                                                    badgeTextColor={theme?.primary}
                                                    badgeContainerStyle={{ paddingHorizontal: 8, paddingVertical: 3 }}
                                                />}
                                                {!!customerHistory?.createdAt && <View style={styles.contactRow}>
                                                    <ZenIcon type='material-community' name="calendar-month-outline" size={14} color={theme?.disabledGray500} />
                                                    <ZenText content={`Joined ${moment(customerHistory.createdAt).format("DD MMM YYYY")}`} size={theme?.errorFontSize} color={theme?.disabledGray500} style={{ marginLeft: 4 }} />
                                                </View>}
                                            </View>
                                        </View>
                                    </View>
                                    {(isOrderRead || isCustomerWrite) && (
                                        <View style={[styles.buttonRow, { marginTop: isLandscape ? 0 : 16 }]}>
                                            {isOrderRead && <ZenButton
                                                title='View Orders'
                                                outlineColor={theme.primary}
                                                type="outline"
                                                icon={<ZenIcon type='ionicon' name="receipt-outline" size={18} color={theme?.primary} style={{ marginRight: 6 }} />}
                                                onPress={() => { navigation.navigate("OrderList", { customerId: params?.customerId }) }}
                                                buttonStyle={[styles.button, { backgroundColor: theme?.light }]}
                                                titleColor={theme?.primary}
                                                containerStyle={{ flex: isLandscape ? 0 : 1, borderRadius: 10 }}
                                            />}
                                            {isCustomerWrite && <ZenButton
                                                title='Edit Customer'
                                                icon={<ZenIcon type='material-community' name="pencil-outline" size={18} color={theme?.light} style={{ marginRight: 6 }} />}
                                                onPress={() => { navigation.navigate("AddEditCustomer", { customerId: params?.customerId, routeName: routeName, userId: customerDetails?.user?.userId }) }}
                                                buttonStyle={[styles.button, { backgroundColor: theme?.primary }]}
                                                titleColor={theme?.light}
                                                containerStyle={{ flex: isLandscape ? 0 : 1, borderRadius: 10 }}
                                            />}
                                        </View>
                                    )}
                                </View>

                                {/* -- Details & Addresses -- */}
                                <View style={{ flexDirection: isLandscape ? 'row' : 'column', alignItems: isLandscape ? 'flex-start' : 'stretch', gap: isLandscape ? 16 : 12 }}>
                                    <SectionCard title="Personal Information" icon="account-details-outline">
                                        <InfoRow label="Email" value={customerDetails?.user?.email} icon="email-outline" />
                                        <InfoRow label="Phone" value={customerDetails?.user?.phone} icon="phone-outline" />
                                        <InfoRow label="Customer Code" value={customerDetails?.customerCode} icon="card-account-details-outline" />
                                        <InfoRow label="Gender" value={customerDetails?.user?.gender} icon="gender-male-female" />
                                        <InfoRow label="Date of Birth" value={customerDetails?.user?.dateOfBirth ? moment(customerDetails?.user?.dateOfBirth).format("DD MMM YYYY") : ''} icon="cake-variant-outline" isLast />
                                        <View style={styles.notesBox}>
                                            <ZenText content="Notes" size={theme?.errorFontSize} color={theme?.disabledGray500} />
                                            <ZenText content={customerDetails?.customerNotes || 'No notes added'} size={theme?.errorFontSize} color={customerDetails?.customerNotes ? theme?.lightDark : theme?.disabledGray500} style={{ marginTop: 4, lineHeight: 20 }} />
                                        </View>
                                    </SectionCard>

                                    <SectionCard title="Addresses" icon="map-marker-multiple-outline" count={addressRows?.length}>
                                        {addressRows?.length ? (
                                            addressRows.map((item: CustomerAddress) => <AddressCard key={item?.id?.toString()} item={item} />)
                                        ) : (
                                            <ZenEmptyComponent text="No addresses found" />
                                        )}
                                    </SectionCard>
                                </View>
                            </ScrollView>
                        )}
                    </View>
                )
            }}
        </ThemeConsumer>
    )
}

const style = (theme: Theme) => StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme?.iceWhite,
    },
    scrollContent: {
        paddingBottom: Platform.OS === 'ios' ? 40 : 24,
        gap: 16,
    },
    profileCard: {
        backgroundColor: theme?.light,
        borderRadius: 16,
        padding: 16,
        borderWidth: 1,
        borderColor: theme?.coolGray,
    },
    avatar: {
        width: 56,
        height: 56,
        borderRadius: 28,
        backgroundColor: theme?.primary,
        justifyContent: 'center',
        alignItems: 'center',
    },
    metaRow: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 10,
        marginTop: 6,
    },
    buttonRow: {
        flexDirection: 'row',
        gap: 12,
    },
    button: {
        borderRadius: 10,
        paddingHorizontal: 18,
        paddingVertical: 10,
    },
    sectionCard: {
        backgroundColor: theme?.light,
        borderRadius: 16,
        padding: 16,
        borderWidth: 1,
        borderColor: theme?.coolGray,
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingBottom: 12,
        marginBottom: 4,
        borderBottomWidth: 1,
        borderBottomColor: theme?.softSlate,
    },
    countBadge: {
        minWidth: 24,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 12,
        alignItems: 'center',
        backgroundColor: theme?.tealPrimary,
    },
    infoRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 12,
    },
    rowDivider: {
        borderBottomWidth: 1,
        borderBottomColor: theme?.softSlate,
    },
    infoIconBox: {
        width: 36,
        height: 36,
        borderRadius: 10,
        backgroundColor: theme?.tealPrimary,
        justifyContent: 'center',
        alignItems: 'center',
    },
    notesBox: {
        marginTop: 8,
        padding: 12,
        borderRadius: 10,
        backgroundColor: theme?.iceWhite,
        borderWidth: 1,
        borderColor: theme?.coolGray,
    },
    addressCard: {
        marginTop: 12,
        padding: 12,
        borderRadius: 12,
        backgroundColor: theme?.iceWhite,
        borderWidth: 1,
        borderColor: theme?.coolGray,
    },
    defaultBadge: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
        backgroundColor: theme?.lightPrimary,
    },
    addressBody: {
        marginTop: 10,
        paddingTop: 10,
        borderTopWidth: 1,
        borderTopColor: theme?.coolGray,
    },
    contactRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
});


export default ViewCustomerDetails;
